import { useEffect, useState } from "react";
import { useIsMobile } from "./use-mobile";

const MOBILE_MIN_ROWS = 3;
const MOBILE_MAX_ROWS = 10;

interface UsePaginationSizeOptions {
  minRows?: number;
  maxRows?: number;
  estimatedRowHeight?: number;
  uiOverheadHeight?: number;
  paginationHeight?: number;
}

function computeScreenFitRows(
  isMobile: boolean | undefined,
  { minRows, maxRows, estimatedRowHeight, uiOverheadHeight, paginationHeight }: Required<UsePaginationSizeOptions>
): number {
  if (isMobile === undefined) {
    return minRows;
  }

  const availableHeight = Math.max(0, window.innerHeight - uiOverheadHeight - paginationHeight);
  const calculatedRows = Math.floor(availableHeight / estimatedRowHeight);

  if (isMobile) {
    return Math.max(MOBILE_MIN_ROWS, Math.min(calculatedRows, MOBILE_MAX_ROWS));
  }

  return Math.max(minRows, Math.min(calculatedRows, maxRows));
}

/** Rows that fit the current viewport height. Pure screen-based default — does not know about any user selection. */
export function usePaginationSize(options: UsePaginationSizeOptions = {}) {
  const { minRows = 5, maxRows = 50, estimatedRowHeight = 50, uiOverheadHeight = 270, paginationHeight = 64 } = options;
  const resolved = { minRows, maxRows, estimatedRowHeight, uiOverheadHeight, paginationHeight };

  const isMobile = useIsMobile();
  const [paginationSize, setPaginationSize] = useState<number>(() => computeScreenFitRows(isMobile, resolved));

  useEffect(() => {
    const recalculate = () => setPaginationSize(computeScreenFitRows(isMobile, resolved));

    recalculate();
    window.addEventListener("resize", recalculate);
    return () => window.removeEventListener("resize", recalculate);
  }, [isMobile, minRows, maxRows, estimatedRowHeight, uiOverheadHeight, paginationHeight]);

  return paginationSize;
}
